import { create } from 'zustand';

export interface WorkHours {
  startHour: number; // 0-23
  startMinute: number; // 0-59
  endHour: number;
  endMinute: number;
  workDays: number[]; // 0=Sunday, 1=Monday, etc.
}

const STORAGE_KEY = 'ondeck-work-hours';

const DEFAULT_WORK_HOURS: WorkHours = {
  startHour: 9,
  startMinute: 0,
  endHour: 17,
  endMinute: 0,
  workDays: [1, 2, 3, 4, 5], // Monday-Friday
};

function getStoredWorkHours(): WorkHours {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_WORK_HOURS, ...JSON.parse(stored) };
    }
  } catch {
    // localStorage not available
  }
  return DEFAULT_WORK_HOURS;
}

function storeWorkHours(hours: WorkHours) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(hours));
  } catch {
    // localStorage not available
  }
}

interface WorkHoursState {
  workHours: WorkHours;
  setWorkHours: (hours: Partial<WorkHours>) => void;
  isWorkingTime: () => boolean;
  isWorkDay: () => boolean;
}

export const useWorkHoursStore = create<WorkHoursState>((set, get) => ({
  workHours: getStoredWorkHours(),

  setWorkHours: (hours: Partial<WorkHours>) => {
    const newHours = { ...get().workHours, ...hours };
    storeWorkHours(newHours);
    set({ workHours: newHours });
  },

  isWorkDay: () => {
    const { workDays } = get().workHours;
    const today = new Date().getDay();
    return workDays.includes(today);
  },

  isWorkingTime: () => {
    const { startHour, startMinute, endHour, endMinute, workDays } = get().workHours;
    const now = new Date();
    const today = now.getDay();

    // Check if it's a work day
    if (!workDays.includes(today)) {
      return false;
    }

    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  },
}));

// Helper to format time for display
export function formatTime(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  const displayMinute = minute.toString().padStart(2, '0');
  return `${displayHour}:${displayMinute} ${period}`;
}

// Encouraging messages for work time
export const WORK_TIME_GREETINGS = [
  "Go get 'em, tiger!",
  "Time to make some calls!",
  "Let's close some deals!",
  "Your contacts are waiting!",
  "Today's the day!",
  "Make it happen!",
  "You've got this!",
  "Let's build those relationships!",
  "Time to shine!",
  "Ready to rock!",
];

// Get a consistent daily greeting (same one all day)
export function getDailyEncouragement(): string {
  const today = new Date();
  const dayIndex = today.getFullYear() * 1000 + today.getMonth() * 100 + today.getDate();
  return WORK_TIME_GREETINGS[dayIndex % WORK_TIME_GREETINGS.length];
}
